import { cn } from '@neo/lib/utils'
import type { ReactNode } from 'react'
import type { OutputState } from '../types'

interface PathPickerProps {
  // Unconnected paths to offer. States carrying more than one distinct
  // `section` render grouped under section sub-headers (fixed-outcome nodes
  // like screening); everything else stays a flat list.
  opStates: OutputState[]
  title?: string
  onSelect: (state: OutputState) => void
}

// One surface for every opener (inline pill panel, edge popover, drag-out
// menu) so the picker looks identical however it was summoned.
export const PATH_PICKER_SURFACE = 'bg-background shadow-sh1 rounded-md min-w-[170px] border-0'

/** Single source of the "Select a Path" row list — shared by the edge chip
 * popover and the node add-path menus. Content only; the opener mounts it on
 * PATH_PICKER_SURFACE. */
export const PathPicker = ({ opStates, title = 'Select a Path', onSelect }: PathPickerProps) => {
  const renderRow = (state: OutputState) => (
    <button
      type="button"
      key={state.id}
      onClick={(e) => {
        e.stopPropagation()
        onSelect(state)
      }}
      className="flex flex-col gap-0.5 px-2 py-1 w-full text-left rounded-md cursor-pointer hover:bg-hv2"
      data-testid={`button-edge-opstate-item-${state.id}`}
    >
      <span className="text-xs-400 text-foreground">{state.name}</span>
      {state.description && (
        <span className="text-xxs-400 text-secondary">{state.description}</span>
      )}
    </button>
  )

  const sections = new Map<string, OutputState[]>()
  for (const state of opStates) {
    const key = state.section ?? ''
    const bucket = sections.get(key) ?? []
    bucket.push(state)
    sections.set(key, bucket)
  }

  let body: ReactNode
  if (opStates.length === 0) {
    body = <div className="px-4 py-2 text-xxs-400 text-tertiary">No paths available</div>
  } else if (sections.size > 1) {
    body = [...sections.entries()].map(([label, states], index) => (
      <div key={label || 'other'}>
        <div
          className={cn(
            'px-2 pb-1 font-jetbrains text-label text-tertiary uppercase',
            index > 0 && 'mt-1 pt-2 border-t border-st1',
          )}
        >
          {label || 'Other'}
        </div>
        {states.map(renderRow)}
      </div>
    ))
  } else {
    body = opStates.map(renderRow)
  }

  return (
    <div className="py-2 pb-1 px-1 max-w-[240px]">
      <div className="px-2 pb-2 text-xxs-600 text-secondary uppercase">{title}</div>
      {body}
    </div>
  )
}
